import { useState } from 'react';
import api from '../../API';
import styles from './CreateFolderModal.module.css';

const FOLDER_TYPES = [
    {
        value: 'organization',
        label: 'Folder',
        description: 'A folder for grouping projects.',
    },
    {
        value: 'project',
        label: 'Project',
        description: 'A folder that represents a single project.',
    },
];

const SUBFOLDER_TYPES = [
    {
        value: 'project-subfolder',
        label: 'Subfolder',
        description: 'A subfolder within the project for grouping items.',
    },
];

/**
 * Modal for creating a new folder within the GirderProjects tree.
 *
 * Props:
 *   isOpen        — boolean
 *   onClose       — () => void
 *   parentId      — string (ID of the parent collection or folder)
 *   parentType    — 'collection' | 'folder'
 *   parentFolderType — girderProjects.type of the parent folder, or null for collection root
 *   onCreated     — (newFolder) => void  called on success
 *   mode          — 'project' | 'folder' | 'subfolder' | 'auto' (default 'auto')
 */
function CreateFolderModal({
    isOpen,
    onClose,
    parentId,
    parentType,
    parentFolderType,
    onCreated,
    mode = 'auto',
}) {
    const [name, setName] = useState('');
    const [folderType, setFolderType] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const isProjectContext = parentFolderType === 'project' || parentFolderType === 'project-subfolder';

    let fixedFolderType = null;
    let title;
    let submitLabel;

    if (mode === 'project') {
        fixedFolderType = 'project';
        title = 'New project';
        submitLabel = submitting ? 'Creating project…' : 'Create project';
    } else if (mode === 'folder') {
        fixedFolderType = isProjectContext ? 'project-subfolder' : 'organization';
        title = isProjectContext ? 'New subfolder' : 'New folder';
        submitLabel = submitting ? 'Creating folder…' : 'Create folder';
    } else if (mode === 'subfolder') {
        fixedFolderType = 'project-subfolder';
        title = 'New subfolder';
        submitLabel = submitting ? 'Creating subfolder…' : 'Create subfolder';
    } else {
        // auto mode — fall back to parent context
        if (isProjectContext) {
            title = 'New subfolder';
        } else {
            title = 'New folder';
        }
        submitLabel = submitting ? 'Creating…' : 'Create';
    }

    // Determine which folder type options to show based on context when not fixed by mode
    const typeOptions = fixedFolderType
        ? []
        : (isProjectContext ? SUBFOLDER_TYPES : FOLDER_TYPES);

    // Auto-select when only one option (and no fixed type)
    const effectiveFolderType = fixedFolderType
        || folderType
        || (typeOptions.length === 1 ? typeOptions[0].value : folderType);

    function handleClose() {
        setName('');
        setFolderType('');
        setError(null);
        onClose();
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Please enter a folder name.');
            return;
        }
        if (!effectiveFolderType) {
            setError('Please select a folder type.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await api.post('projects/folders', {
                query: {
                    parentId,
                    parentType,
                    name: trimmedName,
                    folderType: effectiveFolderType,
                },
            });
            if (response.status === 200 || response.status === 201) {
                onCreated(response.data);
                handleClose();
            } else {
                setError(response.data?.message || 'Failed to create folder.');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'An unexpected error occurred.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>{title}</h3>
                    <button className={styles.closeButton} onClick={handleClose} title="Close">
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="folderName">
                                Folder name
                            </label>
                            <input
                                id="folderName"
                                type="text"
                                className={styles.textInput}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter folder name"
                                autoFocus
                                disabled={submitting}
                            />
                        </div>

                        {typeOptions.length > 1 && (
                            <div className={styles.fieldGroup}>
                                <span className={styles.fieldLabel}>Folder type</span>
                                <div className={styles.radioGroup}>
                                    {typeOptions.map((opt) => (
                                        <label key={opt.value} className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="folderType"
                                                value={opt.value}
                                                checked={effectiveFolderType === opt.value}
                                                onChange={() => setFolderType(opt.value)}
                                                disabled={submitting}
                                            />
                                            <div>
                                                <div>{opt.label}</div>
                                                <div className={styles.radioDescription}>
                                                    {opt.description}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className={styles.errorMessage}>{error}</div>
                        )}
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={submitting || !name.trim() || !effectiveFolderType}
                        >
                            {submitLabel}
                        </button>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateFolderModal;
